/**
 * EmptyState — the "nothing here yet" block we drop into every list page.
 *
 * Why it matters: a blank table screams "the app is broken." A friendly
 * empty state with an explicit CTA teaches the user what the feature does
 * and how to fill it — the same panel doubles as onboarding.
 *
 * Two action shapes are supported so pages don't have to wrap EmptyState
 * in a router/router-link boilerplate:
 *   - { onClick }  — opens a modal, triggers an action, etc.
 *   - { to }       — react-router Link (e.g. "/employees/new")
 *
 * ``variant`` toggles between a padded standalone block (default) and a
 * flush inline layout that slots inside a DataTable cell without doubling
 * its padding.
 */
import { type LucideIcon, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { Button, buttonClasses } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

type EmptyStateAction =
  | { label: string; onClick: () => void; to?: never }
  | { label: string; to: string; onClick?: never };

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  // A second, lower-emphasis action — e.g. "Read the docs" beside
  // "Invite your first employee."
  secondaryAction?: EmptyStateAction;
  // "inline" drops the outer py-16 so the component can live inside a
  // DataTable cell without stacking padding on top of the table's own.
  variant?: "default" | "inline";
  className?: string;
}

function renderAction(
  action: EmptyStateAction,
  variant: "primary" | "secondary"
) {
  if ("to" in action && action.to) {
    return (
      <Link to={action.to} className={buttonClasses(variant)}>
        {action.label}
      </Link>
    );
  }
  return (
    <Button variant={variant} onClick={action.onClick}>
      {action.label}
    </Button>
  );
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  secondaryAction,
  variant = "default",
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        variant === "default" ? "py-16" : "py-10",
        className
      )}
      // aria-live=polite: when an async list resolves to zero items, a
      // screen reader hears the title — same signal a sighted user gets
      // from the icon + heading swap.
      aria-live="polite"
    >
      <div className="rounded-full bg-accent/10 p-4 mb-4">
        <Icon className="h-8 w-8 text-accent" aria-hidden="true" />
      </div>
      <h3 className="text-lg font-semibold font-heading text-text-primary">
        {title}
      </h3>
      {description && (
        <p className="text-sm text-text-muted mt-1 max-w-sm">{description}</p>
      )}
      {(action || secondaryAction) && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {action && renderAction(action, "primary")}
          {secondaryAction && renderAction(secondaryAction, "secondary")}
        </div>
      )}
    </div>
  );
}
